import { AnimatePresence, motion } from "framer-motion";
import { X, Gift } from "lucide-react";

/**
 * One-shot exit-intent save: when the shopper tries to close the cart with items in it,
 * we show this card instead of actually closing. They can:
 *   - Hit "Keep my 10% off" → applies `SAVE10` promo code to the cart and dismisses the card
 *   - Hit "No thanks" → actually closes the cart
 * The parent is responsible for the one-shot gating (sessionStorage flag) so we never
 * nag the same visitor twice.
 */
export default function ExitIntentSave({ open, onApply, onDismiss }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative m-4 w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl"
          >
            <button
              onClick={onDismiss}
              className="absolute right-3 top-3 rounded-full p-1.5 text-tracksy-sub hover:bg-tracksy-cream"
              aria-label="Dismiss"
              data-testid="exit-intent-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-tracksy-orange/10">
              <Gift className="h-7 w-7 text-tracksy-orange" />
            </div>
            <p className="mt-4 text-[11px] font-extrabold uppercase tracking-[0.22em] text-tracksy-orange">
              Wait — one-time offer
            </p>
            <h4 className="mt-2 font-heading text-2xl font-extrabold leading-tight text-tracksy-ink">
              Here's 10% off your Tracksy order.
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-tracksy-sub">
              No sign-up. Clicks apply the code at checkout — vanishes if you leave this cart.
            </p>
            <div className="mt-5 rounded-xl border-2 border-dashed border-tracksy-orange/40 bg-tracksy-orange/5 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-tracksy-sub">Code</p>
              <p className="mt-0.5 font-mono text-lg font-extrabold text-tracksy-ink">SAVE10</p>
            </div>
            <button
              onClick={onApply}
              data-testid="exit-intent-apply"
              className="mt-5 w-full rounded-full bg-tracksy-orange py-3.5 text-sm font-extrabold text-white shadow-glow hover:bg-tracksy-orangeHover"
            >
              Keep my 10% off
            </button>
            <button
              onClick={onDismiss}
              data-testid="exit-intent-decline"
              className="mt-2 w-full text-xs font-semibold text-tracksy-muted hover:text-tracksy-ink"
            >
              No thanks, close anyway
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
